import { DoCheck, inject, Component, OnDestroy, OnInit } from '@angular/core';
import { ActionButtonVisibilityService } from '@netfoundry-ui/feature/action-button';
import { OidcAudience } from '@netfoundry-ui/shared/model';
import { RecordDetailAction, RecordDetailSection } from '@netfoundry-ui/ui/detail-drawer';
import {
    forgetFixedColumnWidths,
    listActionButtonsColumn,
    LocalListTable,
    oidcListTabs,
    ROW_MENU_ICONS,
} from '@netfoundry-ui/ui/table';
import { escape } from 'lodash';
import { Subscription } from 'rxjs';
import { DataTableFilterService, ListCellContext, ListColumn, ListSortDir } from 'ziti-console-lib';
import { OidcAudiencesComponent } from './oidc-audiences.component';

const TABLE_ID = 'oidc-audiences-list';
const SEARCH_FILTER = 'audience';
const DEFAULT_PAGE_SIZE = 30;

function formatDate(value: string | undefined): string {
    const date = value ? new Date(value) : undefined;
    return date && !isNaN(date.getTime()) ? date.toLocaleString() : value || '';
}

type AudienceRow = OidcAudience & { name?: string; actionList?: string[]; hideSelect?: boolean };

/** The organization ids an audience is restricted to, as one line of text. */
export function restrictedOrgText(value: unknown): string {
    if (value === null || value === undefined) {
        return '';
    }
    return Array.isArray(value) ? value.join(', ') : String(value);
}

/** The text a column sorts by, lower-cased. */
export function audienceSortValue(row: Partial<AudienceRow>, key: string): string {
    switch (key) {
        case 'restrictedOrgIds':
            return restrictedOrgText(row.restrictedToOrganizationIds).toLowerCase();
        case 'active':
            return row.active ? 'active' : 'inactive';
        case 'oidcIssuerId':
            return (row.oidcIssuerId || '').toLowerCase();
        default:
            return (row.audience || '').toLowerCase();
    }
}

export function sortAudiences<T extends Partial<AudienceRow>>(rows: T[], key: string, dir: ListSortDir): T[] {
    const sign = dir === 'desc' ? -1 : 1;
    return [...rows].sort((a, b) => sign * audienceSortValue(a, key).localeCompare(audienceSortValue(b, key)));
}

@Component({
    selector: 'app-oidc-audiences-list',
    templateUrl: './oidc-audiences-list.component.html',
    styleUrls: ['./oidc-audiences-list.component.scss'],
    standalone: false,
})
export class OidcAudiencesListComponent extends OidcAudiencesComponent implements OnInit, OnDestroy, DoCheck {
    readonly tableId = TABLE_ID;
    readonly searchFilter = SEARCH_FILTER;
    readonly initialPageSize = DEFAULT_PAGE_SIZE;
    readonly tableOptions = { pager: 'pages' as const };
    readonly menuItems = [
        { label: 'Edit', action: 'update', icon: ROW_MENU_ICONS.edit },
        { label: 'Delete', action: 'delete', icon: ROW_MENU_ICONS.delete },
    ];
    readonly headerActions = [{ label: 'Delete Selected', action: 'deleteSelected', icon: ROW_MENU_ICONS.delete }];

    readonly tabs = oidcListTabs();

    listColumns: ListColumn<AudienceRow>[] = [];
    selectedId = '';
    readonly list = new LocalListTable<AudienceRow>(
        inject(DataTableFilterService),
        TABLE_ID,
        DEFAULT_PAGE_SIZE,
        SEARCH_FILTER,
        () => this.refresh()
    );

    readonly createVisibility = inject(ActionButtonVisibilityService);
    private readonly listSub = new Subscription();
    private sortState: { key: string; dir: ListSortDir } = { key: SEARCH_FILTER, dir: 'asc' };
    private syncedItems: OidcAudience[] | undefined;

    get accessDenied(): boolean {
        return !this.authorizationService.canListOIDCAudience();
    }

    get canCreate(): boolean {
        return this.authorizationService.canCreateOIDCAudience() && !this.createVisibility.isCancelledAccount;
    }

    get selected(): AudienceRow | undefined {
        return this.selectedId ? this.list.rows.find((row) => row.id === this.selectedId) : undefined;
    }

    get rows(): AudienceRow[] {
        this.list.rows.forEach((row: any) => (row.open = row.id === this.selectedId));
        return this.list.rows;
    }

    get selectedIndex(): number {
        return this.list.rows.findIndex((row) => row.id === this.selectedId);
    }

    get hasSelection(): boolean {
        return this.list.rows.some((row) => row.selected);
    }

    override ngOnInit(): void {
        this.listColumns = this.buildColumns();
        forgetFixedColumnWidths(TABLE_ID, this.listColumns);
        this.listSub.add(this.list.connect());
        this.isLoading = true;
        this.handlePermissionCheck();
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        this.listSub.unsubscribe();
    }

    // The legacy loader replaces oidcAudiences on every read, so each new array is sorted and handed to the table here.
    ngDoCheck(): void {
        if (this.oidcAudiences !== this.syncedItems) {
            this.syncedItems = this.oidcAudiences;
            this.syncRows();
        } else {
            this.selectedIds = this.oidcAudiences.filter((row) => row.selected).map((row) => row.id);
        }
    }

    onHeaderAction(action: string): void {
        if (action === 'add') {
            if (this.canCreate) this.create();
        } else if (action === 'delete') {
            this.deleteSelected();
        }
    }

    select(row: AudienceRow): void {
        this.selectedId = this.selectedId === row.id ? '' : row.id;
    }

    closeDetail(): void {
        this.selectedId = '';
    }

    navigateDetail(step: -1 | 1): void {
        const next = this.list.rows[this.selectedIndex + step];
        if (next) {
            this.selectedId = next.id;
        }
    }

    detailSections(row: AudienceRow): RecordDetailSection[] {
        return [
            {
                title: 'Audience',
                fields: [
                    { label: 'Audience', value: row.audience, mono: true },
                    { label: 'Issuer Id', value: row.oidcIssuerId, mono: true },
                    { label: 'Restricted To Organization Ids', value: restrictedOrgText(row.restrictedToOrganizationIds) },
                    { label: 'Status', value: row.active ? 'Active' : 'Inactive' },
                ],
            },
            {
                title: 'Record',
                fields: [
                    { label: 'Id', value: row.id, mono: true },
                    { label: 'Created', value: formatDate(row.createdAt) },
                    { label: 'Created By', value: row.createdBy, mono: true },
                ],
            },
        ];
    }

    detailActions(row: AudienceRow): RecordDetailAction[] {
        const actions: RecordDetailAction[] = [{ label: 'Edit', action: 'update' }];
        if (row.actionList?.includes('delete')) {
            actions.push({ label: 'Delete', action: 'delete', danger: true });
        }
        return actions;
    }

    onAction(event: { action: string; item?: AudienceRow }): void {
        const row = event.item;
        switch (event.action) {
            case 'create':
                if (this.canCreate) this.create();
                break;
            case 'update':
                if (row) this.open(row);
                break;
            case 'delete':
                if (row) this.deleteSingleItem(row);
                break;
            case 'deleteSelected':
                this.deleteSelected();
                break;
        }
    }

    private deleteSelected(): void {
        this.selectedIds = this.oidcAudiences.filter((row) => row.selected).map((row) => row.id);
        if (this.selectedIds.length) {
            this.openConfirm(undefined);
        }
    }

    private syncRows(): void {
        const protectedDelete = this.featureService.enableDeleteProtection;
        const items = this.oidcAudiences.map((row: AudienceRow) => {
            // name is only for the table's search; it stays out of anything that serializes the row
            if (!Object.prototype.hasOwnProperty.call(row, 'name')) {
                Object.defineProperty(row, 'name', { value: row.audience, enumerable: false, writable: true, configurable: true });
            }
            row.hideSelect = protectedDelete || !row.actionList?.includes('delete');
            return row;
        });
        this.list.setItems(sortAudiences(items, this.sortState.key, this.sortState.dir));
    }

    private buildColumns(): ListColumn<AudienceRow>[] {
        const open = (ctx: ListCellContext<AudienceRow>) => this.select(ctx.row);
        const onSort = (key: string, dir: ListSortDir) => {
            this.sortState = { key, dir };
            this.syncRows();
        };
        return [
            {
                key: 'oidcIssuerId',
                label: 'Issuer Id',
                minWidth: 250,
                sortable: true,
                onSort,
                valueFormatter: (shim) => shim.data.oidcIssuerId || '',
                onCellClick: open,
            },
            {
                key: SEARCH_FILTER,
                label: 'Audience',
                minWidth: 250,
                sortable: true,
                sortDir: 'asc',
                onSort,
                filterType: 'TEXTINPUT',
                valueFormatter: (shim) => shim.data.audience || '',
                onCellClick: open,
            },
            {
                key: 'restrictedOrgIds',
                label: 'Restricted To Organization Ids',
                minWidth: 250,
                sortable: true,
                onSort,
                cellHtml: (shim) => escape(restrictedOrgText(shim.data.restrictedToOrganizationIds)),
                onCellClick: open,
            },
            {
                key: 'active',
                label: 'Active',
                width: 130,
                sortable: true,
                onSort,
                cellHtml: (shim) =>
                    `<span class="lt-status lt-status-${shim.data.active ? 'online' : 'offline'}">${shim.data.active ? 'Active' : 'Inactive'}</span>`,
                onCellClick: open,
            },
            listActionButtonsColumn<AudienceRow>({
                menuItems: this.menuItems,
                width: 90,
                cellId: (row) => `OidcAudienceActions${row.id}`,
                run: (action, row) => this.onAction({ action, item: row }),
                buttons: () => [],
            }),
        ];
    }
}
